import axios from "axios";
import { API_BASE_URL, API_TIMEOUT } from "./config";

// Token management utilities
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiration: string;
  refreshTokenLifeTime?: number;
}

const getTokenFromCookie = (): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const cookieString = document.cookie;

  const cookies = cookieString.split("; ");
  const authTokenCookie = cookies.find((cookie) =>
    cookie.startsWith("auth-token="),
  );

  if (authTokenCookie) {
    const token = authTokenCookie.split("=")[1];
    return token;
  }

  return null;
};

const getRefreshTokenFromCookie = (): string | null => {
  if (typeof document === "undefined") return null;

  const cookieString = document.cookie;
  const cookies = cookieString.split("; ");
  const refreshTokenCookie = cookies.find((cookie) =>
    cookie.startsWith("refresh-token="),
  );

  if (refreshTokenCookie) {
    return refreshTokenCookie.split("=")[1];
  }

  return null;
};

const isHomePage = (): boolean => {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/" || path === "/en" || path === "/az" || path === "/ru";
};

const getLocaleFromCookie = (): string => {
  if (typeof document === "undefined") return "en";

  const cookieString = document.cookie;
  const cookies = cookieString.split("; ");
  const localeCookie = cookies.find((cookie) =>
    cookie.startsWith("NEXT_LOCALE="),
  );

  if (localeCookie) {
    return localeCookie.split("=")[1];
  }

  return "en";
};

// İstifadəçinin keçərli (müddəti bitməmiş) auth-token-i olub-olmadığını yoxlayır
export const hasValidAuthToken = (): boolean => {
  const token = getTokenFromCookie();
  if (!token) return false;
  return !isTokenExpired(token);
};

// Set auth tokens in secure cookies (for manual token setting if needed)
export const setAuthTokens = (tokens: AuthTokens): void => {
  if (typeof document === "undefined") {
    return;
  }

  const expirationDate = new Date(tokens.expiration);
  const isSecure = window.location.protocol === "https:";

  // Set access token - Remove secure flag for localhost development
  const accessTokenCookie = `auth-token=${tokens.accessToken}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
  document.cookie = accessTokenCookie;

  // Set refresh token (longer expiration) - Remove secure flag for localhost development
  const refreshExpiration = new Date();
  refreshExpiration.setHours(
    refreshExpiration.getHours() + (tokens.refreshTokenLifeTime || 8760),
  );
  const refreshTokenCookie = `refresh-token=${tokens.refreshToken}; expires=${refreshExpiration.toUTCString()}; path=/; SameSite=Lax`;
  document.cookie = refreshTokenCookie;

  // Verify cookies were set
};

// Note: Backend automatically sets refreshToken cookie via Set-Cookie header
// We only need to manually set accessToken if it's not set by backend

// Clear all auth cookies
export const clearAuthCookies = (): void => {
  if (typeof document === "undefined") return;

  const cookieOptions = `expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;

  document.cookie = `auth-token=; ${cookieOptions}`;
  document.cookie = `refresh-token=; ${cookieOptions}`;
};

// Qeydiyyat token-i (X-Registration-Token) — SIMA təsdiqinə qədər saxlanmış məlumat sorğularında göndərilir
const REGISTRATION_TOKEN_KEY = "registration-token";

export const setRegistrationToken = (token: string): void => {
  try {
    sessionStorage.setItem(REGISTRATION_TOKEN_KEY, token);
  } catch {
    // iqnor
  }
};

export const getRegistrationToken = (): string | null => {
  try {
    return sessionStorage.getItem(REGISTRATION_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const clearRegistrationToken = (): void => {
  try {
    sessionStorage.removeItem(REGISTRATION_TOKEN_KEY);
  } catch {
    // iqnor
  }
};

// Check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch {
    return true;
  }
};

// Refresh token function
const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = getRefreshTokenFromCookie();

  if (!refreshToken) {
    return null;
  }

  try {
    // Create a separate axios instance for refresh token to avoid circular dependency
    const refreshClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
    });

    const response = await refreshClient.post("/Companies/web/refresh-token", {
      refreshToken,
    });

    if (response.data?.responseValue?.accessToken) {
      // Backend will set new refreshToken cookie via Set-Cookie header
      // We only need to set the new accessToken
      const newAccessToken = response.data.responseValue.accessToken.token;
      const expirationDate = new Date(
        response.data.responseValue.accessToken.expiration,
      );

      document.cookie = `auth-token=${newAccessToken}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;

      return newAccessToken;
    } else {
    }
  } catch (error) {
    clearAuthCookies();
  }

  return null;
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
});

// Request interceptor
apiClient.interceptors.request.use(
  async (config) => {
    const locale = getLocaleFromCookie();
    let token = getTokenFromCookie();

    // Check if token exists and is not expired
    if (token && isTokenExpired(token)) {
      // Try to refresh token
      const newToken = await refreshAccessToken();
      token = newToken;
      if (newToken) {
      } else {
      }
    } else if (!token) {
      // Try to refresh token if no token exists
      const newToken = await refreshAccessToken();
      token = newToken;
      if (newToken) {
      } else {
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
    }

    // Qeydiyyat token-i varsa X-Registration-Token başlığı ilə göndər
    const regToken = getRegistrationToken();
    if (regToken) {
      config.headers["X-Registration-Token"] = regToken;
    }

    // Add Accept-Language header
    config.headers["Accept-Language"] = locale;

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor with automatic token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh token
      const newToken = await refreshAccessToken();

      if (newToken) {
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } else {
        // Refresh failed, clear cookies and redirect to login
        clearAuthCookies();
        if (typeof window !== "undefined" && !isHomePage()) {
          window.location.href = "/login";
        }
      }
    }

    // Approval doc bölmə 6: giriş filtri real HTTP 403 qaytarır, handler xətaları isə
    // HTTP 400 ilə gəlib əsl statusu error.statusCode-da saxlayır. Hər ikisini yoxlayırıq.
    if (typeof window !== "undefined") {
      try {
        const body = (error.response?.data as any)?.error;
        const innerStatus = body?.statusCode;
        const code: string | undefined = body?.code;
        const is403 =
          error.response?.status === 403 ||
          (error.response?.status === 400 && innerStatus === 403);

        if (is403) {
          const localeCookie = document.cookie
            .split("; ")
            .find((c) => c.startsWith("NEXT_LOCALE="));
          const locale = localeCookie ? localeCookie.split("=")[1] : "en";
          const path = window.location.pathname;

          // Doc 6: şirkət bloklanıb — hər yerdən çıxış, girişə yönlət
          if (code === "CompanyBlocked") {
            clearAuthCookies();
            try {
              sessionStorage.setItem(
                "company-blocked",
                JSON.stringify(body?.data ?? {}),
              );
            } catch {
              // iqnor
            }
            if (!isHomePage() && !path.includes("/login")) {
              window.location.href = `/${locale}/login`;
            }
            return Promise.reject(error);
          }

          // Doc 4.4/6: profil natamam (isProfileComplete=false) və ya şirkət hələ
          // təsdiqlənməyib (CompanyNotApproved) — yalnız profil bölməsi açıqdır.
          const stored = localStorage.getItem("user");
          const u = stored ? JSON.parse(stored) : null;
          const notApproved = code === "CompanyNotApproved";
          if (
            (notApproved || u?.isProfileComplete === false) &&
            !path.includes("/profile")
          ) {
            window.location.href = `/${locale}/profile`;
            return Promise.reject(error);
          }
        }
      } catch {
        // iqnor — tor işləməsə adi xəta qaytarılır
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
